import {useState} from 'react';
import {History, RotateCcw} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@kroll-resolver-crisp/ui-components-react';
import type {GroundingRevision} from '../../api/workflows';

export function HistoryPanel({
  revisions,
  canRestore,
  busy,
  onRestore
}: {
  readonly revisions: GroundingRevision[];
  readonly canRestore: boolean;
  readonly busy: boolean;
  readonly onRestore: (id: string) => void;
}) {
  const [ask, setAsk] = useState<GroundingRevision | null>(null);
  const sorted = [...revisions].sort((a, b) => b.number - a.number);

  if (sorted.length === 0) {
    return (
      <Empty className="border-border border p-6">
        <EmptyHeader>
          <History aria-hidden className="text-muted-foreground size-5" />
          <EmptyTitle>No grounding revisions yet</EmptyTitle>
          <EmptyDescription>Every published grounding set is kept here.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <section className="flex flex-col gap-2">
      <div>
        <h3 className="text-sm font-semibold">Grounding revisions</h3>
        <p className="text-muted-foreground text-xs">
          Every published grounding set is kept. Making one active serves it again straight away.
        </p>
      </div>
      <div className="border-border overflow-x-auto rounded border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Revision</TableHead>
              <TableHead>Published</TableHead>
              <TableHead>By</TableHead>
              <TableHead className="text-right">Entries</TableHead>
              <TableHead className="w-36" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((revision) => (
              <TableRow key={revision.id}>
                <TableCell className="font-medium">{`#${revision.number} · ${revision.source}`}</TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {new Date(revision.created_at).toLocaleString()}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {revision.created_by}
                </TableCell>
                <TableCell className="text-right tabular-nums">{revision.entry_count}</TableCell>
                <TableCell className="text-right">
                  {revision.active ? (
                    <Badge variant="secondary">Serving</Badge>
                  ) : canRestore ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => {
                        setAsk(revision);
                      }}>
                      <RotateCcw aria-hidden className="size-4" />
                      Make active
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <AlertDialog
        open={ask !== null}
        onOpenChange={(open) => {
          if (!open) {
            setAsk(null);
          }
        }}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>{ask ? `Make revision #${ask.number} active?` : ''}</AlertDialogTitle>
            <AlertDialogDescription>
              {ask
                ? `RD switches to these ${ask.entry_count} entries on its next load. The current revision stays in this list so you can switch back.`
                : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (ask) {
                  onRestore(ask.id);
                }

                setAsk(null);
              }}>
              Confirm rollback
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
